/**
 * D-274 §1 — the ONE rule of every customer picker and customer filter: the
 * company (OTE, `organizations.is_internal`) is shown as "עבודה פנימית OTE"
 * (i18n `engagementChain.internalWork`), FIRST, and visually apart from the
 * customers (a group heading "לקוחות" in a searchable picker, an <optgroup>
 * in a native <select>). Pure: no React, no requests (pinned by
 * `utils/__tests__/d274-internal-work.test.ts`).
 */
import type { PickerOption } from '../components/ui/entityPickerModel';

/** The i18n key of the company's name in every picker (D-274 §1). */
export const INTERNAL_WORK_LABEL_KEY = 'engagementChain.internalWork';
/** The i18n key of the customers' group heading under it. */
export const CUSTOMERS_GROUP_KEY = 'engagementChain.customersGroup';

export interface OrgLike {
  id: string;
  name: string;
  is_internal?: boolean | null;
}

/** Is this row the company? By its flag, or by the company's id. */
export function isCompanyOrg(org: { id: string; is_internal?: boolean | null }, internalOrgId: string | null | undefined): boolean {
  return org.is_internal === true || (!!internalOrgId && org.id === internalOrgId);
}

/**
 * Split a list of organizations into the company (renamed to `label`) and
 * the customers (in their given order). The company is null when it is not in
 * the list (a filter built from rows that hold none of its work).
 */
export function splitInternalFirst<T extends OrgLike>(
  orgs: readonly T[],
  internalOrgId: string | null | undefined,
  label: string
): { internal: { id: string; name: string } | null; customers: T[] } {
  let internal: { id: string; name: string } | null = null;
  const customers: T[] = [];
  for (const o of orgs) {
    if (isCompanyOrg(o, internalOrgId)) {
      if (!internal) internal = { id: o.id, name: label };
    } else {
      customers.push(o);
    }
  }
  return { internal, customers };
}

/**
 * The options of a searchable picker (`EntityPicker`): "עבודה פנימית OTE"
 * first, then the customers under the group heading `customersGroup` (only
 * when the company is listed, so a plain customer list looks as before).
 */
export function internalFirstPickerOptions<T extends OrgLike>(
  orgs: readonly T[],
  internalOrgId: string | null | undefined,
  label: string,
  customersGroup: string
): PickerOption[] {
  const { internal, customers } = splitInternalFirst(orgs, internalOrgId, label);
  const rest = customers.map((c) => ({ value: c.id, label: c.name, ...(internal ? { group: customersGroup } : {}) }));
  return internal ? [{ value: internal.id, label: internal.name }, ...rest] : rest;
}

/**
 * A list FILTER's options (`{ value, label }`, the value being the customer's
 * id or name): the option whose value is `companyValue` is relabelled `label`
 * and goes first; the others keep their order under `customersGroup`. The
 * value never changes, so the filter itself works as before.
 */
export function internalFirstFilterOptions<T extends { value: string; label: string }>(
  options: readonly T[],
  companyValue: string | null | undefined,
  label: string,
  customersGroup: string
): Array<T & { group?: string }> {
  const company = companyValue ? options.find((o) => o.value === companyValue) : undefined;
  if (!company) return [...options];
  return [
    { ...company, label },
    ...options.filter((o) => o !== company).map((o) => ({ ...o, group: customersGroup })),
  ];
}

/**
 * D-274b §1 — "עבודה פנימית OTE" is offered to every OTE employee, also when
 * none of the listed rows (e.g. "my open tasks") belongs to it. Adds the
 * company when it is missing; its name is replaced by the picker's label.
 */
export function withCompanyListed<T extends { id: string; name: string }>(
  orgs: readonly T[],
  internalOrgId: string | null | undefined
): Array<T | { id: string; name: string }> {
  if (!internalOrgId || orgs.some((o) => o.id === internalOrgId)) return [...orgs];
  return [...orgs, { id: internalOrgId, name: '' }];
}
